import { useState, useMemo, useCallback, useEffect } from "react";
import { cn } from "@apex/shared";
import { WidgetProvider, type WidgetConfig, type ViewType } from "./state";
import { DirtyStateDialog } from "./components/DirtyStateDialog";
import { Header } from "./components/Header";
import { Stepper, type StepperStep } from "./components/Stepper";
import { PortalProvider } from "./components/WidgetPortal";
import {
  LoadingView,
  ItemSelectionView,
  ConfigureView,
  SummaryView,
  SuccessView,
  ErrorView,
  ServiceUnavailableView,
} from "./components/views";
import { useWidget } from "./state";
import { useUnsavedChangesGuard } from "./hooks/useUnsavedChangesGuard";
import { clearPersistedState, useStatePersistence } from "./hooks/useStatePersistence";

interface WidgetContentProps {
  className?: string;
  theme?: string;
  tenantSlug?: string;
  apiKey: string;
}

/**
 * Inner widget content that renders the appropriate view based on state
 */
function WidgetContent({ className, theme, tenantSlug, apiKey }: WidgetContentProps) {
  const { state, dispatch, selectRootItem } = useWidget();
  const [pendingStep, setPendingStep] = useState<ViewType | null>(null);

  // Customer has work-in-progress whenever they've selected accessories and
  // we haven't yet reached the success state.
  const isDirty = state.selectedItems.size > 0 && state.view !== "success";

  useUnsavedChangesGuard(isDirty);

  // Persist state across refresh (sessionStorage) and provide shareable
  // configuration via the URL hash. PII (form fields) never goes in either.
  const resolveModelByCode = useCallback(
    (code: string) => state.rootItems.find((m) => m.code === code) ?? null,
    [state.rootItems]
  );
  const resolveItemIdsByCodes = useCallback(
    (codes: string[]) =>
      state.items.filter((i) => codes.includes(i.code)).map((i) => i.id),
    [state.items]
  );
  useStatePersistence({
    tenantSlug: tenantSlug ?? "default",
    apiKey,
    state,
    dispatch,
    selectRootItem,
    resolveModelByCode,
    resolveItemIdsByCodes,
  });

  // Once the quote has been submitted successfully the persisted state is
  // no longer interesting — clear it so a fresh tab doesn't auto-restore a
  // completed quote.
  useEffect(() => {
    if (state.view === "success") {
      clearPersistedState(tenantSlug ?? "default", apiKey);
    }
  }, [state.view, tenantSlug, apiKey]);

  // ConfigureView and SummaryView manage their own headers; service-unavailable
  // is intentionally chrome-free so the message reads as the entire surface.
  const showHeader =
    state.view !== "configure" &&
    state.view !== "summary" &&
    state.view !== "service-unavailable";

  // Stepper is hidden for non-wizard views
  const showStepper =
    state.view === "selection" ||
    state.view === "configure" ||
    state.view === "summary";

  // Derive step state from current widget state
  const steps = useMemo<StepperStep[]>(() => {
    const hasModel = state.selectedRootItem != null;
    return [
      {
        id: "selection",
        label: "Choose Model",
        reachable: true,
        completed: hasModel,
      },
      {
        id: "configure",
        label: "Configure",
        reachable: hasModel,
        completed: hasModel && state.view === "summary",
      },
      {
        id: "summary",
        label: "Your Details",
        reachable: hasModel,
        completed: false,
      },
    ];
  }, [state.selectedRootItem, state.view]);

  /**
   * Apply a stepper navigation immediately (no dirty guard).
   */
  const applyStepNavigation = useCallback(
    (target: ViewType) => {
      if (target === "selection") {
        dispatch({ type: "BACK_TO_SELECTION" });
        return;
      }
      if (target === "configure" && state.view === "summary") {
        // Going from Summary back to Configure preserves selections.
        dispatch({ type: "BACK_TO_CONFIGURE" });
        return;
      }
      if (target === "summary" && state.view === "configure") {
        dispatch({ type: "VIEW_SUMMARY" });
      }
    },
    [dispatch, state.view]
  );

  const handleStepClick = useCallback(
    (target: ViewType) => {
      if (target === state.view) return;

      // Going BACK to selection from a state with selections is destructive
      // — surface the confirmation dialog instead of silently wiping work.
      const isDestructive =
        target === "selection" &&
        (state.view === "configure" || state.view === "summary") &&
        state.selectedItems.size > 0;

      if (isDestructive) {
        setPendingStep(target);
        return;
      }

      applyStepNavigation(target);
    },
    [applyStepNavigation, state.view, state.selectedItems]
  );

  const handleConfirmDirty = useCallback(() => {
    if (pendingStep) {
      applyStepNavigation(pendingStep);
      setPendingStep(null);
    }
  }, [pendingStep, applyStepNavigation]);

  const handleCancelDirty = useCallback(() => {
    setPendingStep(null);
  }, []);

  const renderView = () => {
    switch (state.view) {
      case "loading":
        return <LoadingView />;
      case "selection":
        return <ItemSelectionView />;
      case "configure":
        return <ConfigureView />;
      case "summary":
        return <SummaryView />;
      case "success":
        return <SuccessView />;
      case "error":
        return <ErrorView />;
      case "service-unavailable":
        return <ServiceUnavailableView />;
      default:
        return <LoadingView />;
    }
  };

  return (
    <div
      data-theme={theme}
      className={cn(
        "apex-widget-root apex-flex apex-flex-col apex-h-full apex-min-h-[500px] apex-overflow-hidden apex-rounded-lg apex-border apex-border-border apex-bg-background",
        className
      )}
    >
      {showHeader && <Header />}
      {showStepper && (
        <Stepper
          steps={steps}
          activeStep={state.view}
          onStepClick={handleStepClick}
        />
      )}
      <div className="apex-widget-view apex-flex-1 apex-overflow-hidden">{renderView()}</div>
      <DirtyStateDialog
        open={pendingStep != null}
        onCancel={handleCancelDirty}
        onConfirm={handleConfirmDirty}
      />
    </div>
  );
}

export interface WidgetProps extends WidgetConfig {
  /** Additional CSS class for the widget container */
  className?: string;
  /**
   * Widget height. Default: `"auto"` — the widget grows to its content and
   * only the host page scrolls (no double-scrollbar). Set an explicit value
   * (`"100vh"`, `"800"`, `"75%"`, etc.) to lock the widget to that height;
   * inner views will then scroll internally if their content overflows.
   */
  height?: string | number;
  /** Widget width (default: 100%) */
  width?: string | number;
}

/**
 * Apex Widget - Embeddable Product Configurator
 *
 * A self-contained widget for configuring products and submitting quote requests.
 *
 * @example
 * ```tsx
 * <Widget
 *   apiUrl="https://api.example.com"
 *   apiKey="apex_wk_..."
 *   theme="light"
 * />
 * ```
 */
export function Widget({
  apiUrl,
  apiKey,
  initialRootItem,
  initialItems,
  theme = "light",
  tenantSlug,
  className,
  height = "auto",
  width = "100%",
}: WidgetProps) {
  const config: WidgetConfig = {
    apiUrl,
    apiKey,
    initialRootItem,
    initialItems,
    theme,
    tenantSlug,
  };

  // Auto height = inline-grow mode (default, recommended for embeds in
  // pages with their own scroll). Fixed height = locked-size mode (the
  // widget is exactly that tall; inner views scroll when content overflows).
  // The data attribute lets the shadow-DOM CSS in base.css branch the
  // inner-overflow rules without threading a prop through every view.
  const isAutoHeight = height === "auto";
  const style: React.CSSProperties = {
    height: isAutoHeight
      ? "auto"
      : typeof height === "number"
        ? `${height}px`
        : height,
    width: typeof width === "number" ? `${width}px` : width,
    position: "relative",
  };

  // Portal container ref via callback (available on first render)
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  return (
    <div
      style={style}
      className={cn("apex-widget-container", className)}
    >
      <WidgetProvider config={config}>
        <PortalProvider value={portalContainer}>
          <WidgetContent theme={theme} tenantSlug={tenantSlug} apiKey={apiKey} />
        </PortalProvider>
      </WidgetProvider>
      {/* Portal target — outside overflow-hidden root, escapes transform containing blocks */}
      <div ref={setPortalContainer} />
    </div>
  );
}

export default Widget;
